import { useNavigate, useSearchParams } from 'react-router-dom'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { apiURL } from '../../../../utilities/URLs'
import { CloudUploadIcon } from 'lucide-react'
import { Document } from '../classes/Document'
import { useEffect, useState } from 'react'
import DocumentRow from './DocumentRow'
import Download8821 from './Download8821'
import DownloadDebitList from './DownloadDebitList'
import DownloadLOD from './DownloadLOD'

export default function DocumentUpload({ applicationId, selectedType, nothingToUpload, setNothingToUpload, handleCheckDocuments, isQuarterScopedUpload = false }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const quarter = searchParams.get('quarter') ?? ''
  const [documents, setDocuments] = useState([])

  const isCategorySkipped = nothingToUpload.includes(selectedType)

  const handleToggleNothingToUpload = () => {
    setNothingToUpload(prev => {
      if (prev.includes(selectedType))
        return prev.filter(key => key !== selectedType)
      return [...prev, selectedType]
    })
  }

  const handleUploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const uploadResponse = await fetch(`${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(selectedType)}&quarter=${quarter}`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken
      },
      body: formData
    })

    const uploadData = await uploadResponse.json()
    if (uploadData.response.error) {
      if (uploadData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUploadFile(file) : navigate('/', { state: { expired: true } })
      } else
        console.error(uploadData.response.error)
    } else {
      setDocuments(prev => [...prev, new Document({
        documentId: uploadData.response.documentId,
        applicationId: applicationId,
        name: file.name,
        size: file.size,
        mimetype: file.type,
        documentURL: uploadData.response.documentURL
      })])

      if (nothingToUpload.includes(selectedType))
        setNothingToUpload(prev => prev.filter(key => key !== selectedType))

      handleCheckDocuments()
    }
  }

  const handleFileUpload = (files) => {
    if (!files) return
    const fileArray = Array.from(files)
    fileArray.forEach(async file => {
      await handleUploadFile(file)
    })
  }

  useEffect(() => {
    const handleGetDocuments = async () => {
      const q = searchParams.get('quarter') ?? ''
      const qParam = q ? `&quarter=${encodeURIComponent(q)}` : ''
      const response = await fetch(`${apiURL}/api/applications/${applicationId}/documents?type=${encodeURIComponent(selectedType)}${qParam}`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken,
        }
      })
      const data = await response.json()
      if (data.response.error) {
        if (data.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken) await handleGetDocuments()
          else navigate('/', { state: { expired: true } })
        } else
          console.error(data.response.error)
      } else
        setDocuments(data.response.documents)
    }

    handleGetDocuments()
  }, [selectedType, quarter, applicationId])

  return (
    <div className='w-full'>
      {selectedType === '8821' && (
        <div className='w-full mb-4'>
          <Download8821 />
        </div>
      )}
      {selectedType === 'debit' && (
        <div className='w-full mb-4'>
          <DownloadDebitList />
        </div>
      )}
      {selectedType === 'letter_dir' && (
        <div className='w-full mb-6'>
          <DownloadLOD />
        </div>
      )}
      {!isQuarterScopedUpload && (
        <label
          htmlFor={`nothing-to-upload-${selectedType}`}
          className='flex items-start gap-3 w-full mb-4 p-4 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 cursor-pointer'
        >
          <input
            id={`nothing-to-upload-${selectedType}`}
            type='checkbox'
            checked={isCategorySkipped}
            onChange={handleToggleNothingToUpload}
            className='mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-ertc-primary focus:ring-ertc-primary'
          />
          <span className='text-sm text-slate-700 dark:text-slate-200'>
            I have nothing to upload for this category
          </span>
        </label>
      )}
      <label htmlFor='file-upload' className={`w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-3xl p-6 md:p-12 flex flex-col items-center justify-center text-center bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-300 cursor-pointer shadow-sm group ${isCategorySkipped ? 'opacity-60' : ''}`}>
        <CloudUploadIcon size={32} className='text-slate-400 group-hover:text-ertc-primary dark:text-slate-500 transition-colors mb-3' />
        <p className='text-xl font-bold text-slate-700 dark:text-gray-100 mb-1 group-hover:text-ertc-primary transition-colors'>Upload Documents</p>
        <p className='text-sm text-slate-500 dark:text-slate-400'>Drag and drop files here or click to browse</p>
      </label>
      <input
        id='file-upload'
        type='file'
        multiple
        className='hidden'
        onChange={(e) => handleFileUpload(e.target.files)}
      />
      <div className='w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 mt-6 transition-colors duration-300 min-h-[150px] flex flex-col justify-center gap-4'>
        {documents.length === 0 ? (
          <p className='text-slate-500 dark:text-slate-400 text-center italic'>
            {isCategorySkipped
              ? 'You indicated you have nothing to upload for this category.'
              : 'No documents uploaded for this category yet.'}
          </p>
        ) : (
          <ul className='space-y-4'>
            {documents.map(doc => (
              <DocumentRow
                key={doc.id}
                _document={doc}
                setDocuments={setDocuments}
                handleCheckDocuments={handleCheckDocuments}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}